import React from "react";
import { motion } from "framer-motion";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import farmersLottie from "../assets/lottie-files/Farmers.lottie";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" as const } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};

const LightCard = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <div style={{ background: "#ffffff", border: "1px solid rgba(45,106,20,0.12)", borderRadius: 16, padding: "1.75rem", boxShadow: "0 2px 20px rgba(0,0,0,0.055)", ...style }}>
    {children}
  </div>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <div style={{ marginBottom: "2rem" }}>
    <h2 style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", fontWeight: 800, color: "#0d1b2a", margin: 0 }}>
      {children}
    </h2>
    <div style={{ width: 56, height: 4, background: "linear-gradient(90deg, #6dc744, #4a8c25)", borderRadius: 2, marginTop: 8 }} />
  </div>
);

const NAV_ITEMS = [
  { label: "At a Glance", href: "#glance" },
  { label: "Land Acknowledgement", href: "#land" },
  { label: "Our Space", href: "#space" },
  { label: "Our History", href: "#history" },
];

const TIMELINE = [
  { year: "Fall 2007", label: "The Spark", desc: "A single local organic farmer rented the Masonic Temple downtown to sell surplus crops — over 500 customers showed up with minimal advertising." },
  { year: "Jan 2008", label: "Steering Committee", desc: "A dedicated group of community members formed a Steering Committee to turn that spark into a permanent weekly market." },
  { year: "Jul–Nov 2008", label: "Weekly Markets Begin", desc: "The volunteer committee organized weekly Saturday markets and commissioned a Consulting Team to chart a sustainable path forward." },
  { year: "2009", label: "Formal Incorporation", desc: "The Friends of the St. John's Farmers' Market incorporated, hired the first Market Manager, and began developing the Co-op structure and by-laws." },
  { year: "Nov 2010", label: "Co-op Founded", desc: "The St. John's Farmers' Market Co-op was officially formed with 25 founding members, with its first AGM held in May 2011." },
  { year: "2018", label: "A New Home", desc: "The market moved into the former Metrobus depot on Freshwater Road — 14,000 sq ft, seven times the size of the previous location." },
  { year: "Today", label: "Growing Strong", desc: "Home to 100+ local vendors, open every Saturday year-round, and continuing to grow as a beloved community hub." },
];

const OurStory: React.FC = () => {
  return (
    <div style={{ fontFamily: "inherit" }}>

      {/* ── HERO ── */}
      <section
        style={{
          background: "linear-gradient(135deg, #0a2605 0%, #0d1b2a 55%, #150308 100%)",
          padding: "5rem 1.5rem 6rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: -120, right: -120, width: 440, height: 440, borderRadius: "50%", background: "radial-gradient(circle, rgba(198,37,45,0.2) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -100, left: -100, width: 380, height: 380, borderRadius: "50%", background: "radial-gradient(circle, rgba(109,199,68,0.15) 0%, transparent 70%)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "3rem", position: "relative", zIndex: 1 }}>
          <motion.div style={{ flex: "1 1 480px", minWidth: 300 }} variants={stagger} initial="hidden" animate="visible">
            <motion.span
              variants={fadeUp}
              style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(109,199,68,0.12)", border: "1px solid rgba(109,199,68,0.3)", borderRadius: 999, padding: "0.35rem 1rem", color: "#6dc744", fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "1rem" }}
            >
              <span style={{ width: 7, height: 7, background: "#6dc744", borderRadius: "50%", display: "inline-block" }} />
              Since 2007
            </motion.span>

            <motion.h1
              variants={fadeUp}
              style={{ fontSize: "clamp(2.4rem, 5.5vw, 4rem)", fontWeight: 900, color: "white", lineHeight: 1.08, marginBottom: "1.25rem" }}
            >
              Our{" "}
              <span style={{ background: "linear-gradient(90deg, #6dc744, #c6252d)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Story
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              style={{ fontSize: "1.15rem", color: "rgba(255,255,255,0.72)", lineHeight: 1.7, marginBottom: "2rem", maxWidth: 520 }}
            >
              Since our founding, the St. John's Farmers' Market has grown into a
              vibrant gathering place for local farmers, artisans, and the broader
              community. Discover our history, our space, and our commitment to
              honouring the land and the people who call it home.
            </motion.p>

            <motion.div variants={fadeUp}>
              <a
                href="#history"
                style={{ display: "inline-block", background: "linear-gradient(135deg, #c6252d, #a01e25)", color: "white", borderRadius: 999, padding: "0.9rem 2.2rem", fontWeight: 700, fontSize: "1rem", cursor: "pointer", textDecoration: "none", boxShadow: "0 4px 20px rgba(198,37,45,0.35)", transition: "transform 0.2s, box-shadow 0.2s" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(198,37,45,0.5)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(198,37,45,0.35)"; }}
              >
                Read Our History →
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.85, ease: "easeOut" }}
            style={{ flex: "1 1 360px", minWidth: 280, display: "flex", justifyContent: "center" }}
          >
            <DotLottieReact src={farmersLottie} loop autoplay style={{ width: "100%", maxWidth: 440 }} />
          </motion.div>
        </div>
      </section>

      {/* ── STICKY NAV ── */}
      <div style={{ position: "sticky", top: 65, zIndex: 90, background: "rgba(255,253,248,0.95)", backdropFilter: "blur(8px)", borderBottom: "1px solid rgba(45,106,20,0.12)", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 1.5rem", display: "flex", gap: "0.25rem", overflowX: "auto", scrollbarWidth: "none" }}>
          {NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              style={{ display: "inline-block", padding: "0.9rem 1.1rem", fontSize: "0.88rem", fontWeight: 600, color: "#2d6a14", textDecoration: "none", borderBottom: "2px solid transparent", whiteSpace: "nowrap", transition: "color 0.2s, border-color 0.2s" }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "#c6252d"; e.currentTarget.style.borderBottomColor = "#c6252d"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "#2d6a14"; e.currentTarget.style.borderBottomColor = "transparent"; }}
            >
              {item.label}
            </a>
          ))}
        </div>
      </div>

      {/* ── AT A GLANCE ── */}
      <section id="glance" style={{ background: "#fffdf8", padding: "5rem 1.5rem", scrollMarginTop: 120 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <SectionHeading>At a Glance</SectionHeading>
            </motion.div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 200px), 1fr))", gap: "1.25rem", maxWidth: 900, margin: "0 auto" }}>
              {[
                { stat: "2007", label: "Year Founded", icon: "🌱", accent: "#6dc744" },
                { stat: "2010", label: "Co-op Formed", icon: "🤝", accent: "#c6252d" },
                { stat: "100+", label: "Local Vendors", icon: "🛒", accent: "#C19A3B" },
                { stat: "Every Saturday", label: "Open Year-Round", icon: "📅", accent: "#6dc744" },
                { stat: "14,000 sq ft", label: "Market Floor Space", icon: "🏛️", accent: "#c6252d" },
                { stat: "245 Freshwater Rd", label: "Our Home Since 2018", icon: "📍", accent: "#C19A3B" },
              ].map(({ stat, label, icon, accent }) => (
                <motion.div key={label} variants={fadeUp} style={{ height: "100%" }}>
                  <LightCard style={{ textAlign: "center", borderTop: `4px solid ${accent}` }}>
                    <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>{icon}</div>
                    <p style={{ margin: "0 0 0.25rem", fontSize: "1.5rem", fontWeight: 900, color: "#0d1b2a" }}>{stat}</p>
                    <p style={{ margin: 0, fontSize: "0.85rem", color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</p>
                  </LightCard>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── LAND ACKNOWLEDGEMENT ── */}
      <section id="land" style={{ background: "#f2fbee", padding: "5rem 1.5rem", scrollMarginTop: 120 }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <SectionHeading>Land Acknowledgement</SectionHeading>
            </motion.div>

            <motion.div variants={fadeUp}>
              <LightCard style={{ borderLeft: "5px solid #6dc744", background: "#ffffff" }}>
                <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", marginBottom: "1.5rem" }}>
                  <span style={{ fontSize: "2.2rem", flexShrink: 0, lineHeight: 1 }}>🌿</span>
                  <p style={{ margin: 0, fontSize: "1.08rem", color: "#2d3748", lineHeight: 1.8 }}>
                    We respectfully acknowledge the land on which we gather as the ancestral homelands of the
                    Beothuk, whose culture has been erased forever. We also acknowledge the island of Ktaqmkuk
                    (Newfoundland) as the unceded, traditional territory of the Beothuk and the Mi'kmaq.
                    Labrador is the traditional and ancestral homelands of the Innu of Nitassinan, the Inuit
                    of Nunatsiavut, and the Inuit of NunatuKavut.
                  </p>
                </div>

                <div style={{ width: "100%", height: 1, background: "rgba(45,106,20,0.1)", margin: "1.25rem 0" }} />

                <p style={{ margin: "0 0 1rem", fontSize: "1.05rem", color: "#2d3748", lineHeight: 1.8 }}>
                  We recognize all First Peoples who were here before us, those who live with us now, and the
                  seven generations to come. As First Peoples have done since time immemorial, we strive to be
                  responsible stewards of the land and to respect the cultures, ceremonies, and traditions of
                  all who call it home.
                </p>

                <p style={{ margin: 0, fontSize: "1.05rem", color: "#2d3748", lineHeight: 1.8 }}>
                  At the St. John's Farmers' Market, we acknowledge that all we do here is built on the
                  foundation of those who came before us. We grow our crops and craft our goods on land that
                  was not originally ours. We strive to create an open and welcoming environment for everyone,
                  including those who are part of the Two-Spirit community. As we open our hearts and minds to
                  the past, we commit ourselves to working in a spirit of truth and reconciliation to build a
                  better future for all.
                </p>
              </LightCard>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── OUR SPACE ── */}
      <section id="space" style={{ background: "#fffdf8", padding: "5rem 1.5rem", scrollMarginTop: 120 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <SectionHeading>Our Space</SectionHeading>
            </motion.div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "2rem", alignItems: "start" }}>
              <motion.div variants={fadeUp} style={{ gridColumn: "1 / -1" }}>
                <LightCard style={{ borderTop: "4px solid #6dc744" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "2rem" }}>
                    <div>
                      <h3 style={{ margin: "0 0 1rem", fontWeight: 800, fontSize: "1.1rem", color: "#0d1b2a" }}>A Home Worth Growing Into</h3>
                      <p style={{ margin: "0 0 1rem", color: "#4a5568", fontSize: "1rem", lineHeight: 1.8 }}>
                        Since 2018, we have called the former Metrobus depot on Freshwater Road our home. At
                        14,000 sq ft — seven times the size of our previous location — our facility offers a
                        welcoming, accessible, and community-focused space for everyone.
                      </p>
                      <p style={{ margin: 0, color: "#4a5568", fontSize: "1rem", lineHeight: 1.8 }}>
                        The larger space allows us to welcome more vendors and gives customers room to
                        socialise, enjoy delicious local food, shop for fresh produce, and discover unique
                        local art. It's everything our old market offered — and so much more.
                      </p>
                    </div>
                    <div>
                      <h3 style={{ margin: "0 0 1rem", fontWeight: 800, fontSize: "1.1rem", color: "#0d1b2a" }}>Our Vision</h3>
                      <p style={{ margin: "0 0 1.25rem", color: "#4a5568", fontSize: "1rem", lineHeight: 1.8 }}>
                        We envision a true community space — complete with a community kitchen, public square,
                        playground, and other amenities. We see SJFM as an essential gathering place in a city
                        that deserves more welcoming public spaces.
                      </p>
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                        {[
                          "Fully accessible with 11 gender-neutral washrooms",
                          "Open to all — vendors, shoppers, and community members",
                          "Home to rental spaces, a commercial kitchen & workshop room",
                        ].map((point) => (
                          <div key={point} style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start" }}>
                            <span style={{ width: 7, height: 7, background: "#6dc744", borderRadius: "50%", flexShrink: 0, marginTop: 6 }} />
                            <span style={{ color: "#4a5568", fontSize: "0.95rem", lineHeight: 1.6 }}>{point}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </LightCard>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── OUR HISTORY (TIMELINE) ── */}
      <section id="history" style={{ background: "#f2fbee", padding: "5rem 1.5rem", scrollMarginTop: 120 }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <SectionHeading>Our History</SectionHeading>
              <p style={{ color: "#4a5568", fontSize: "1.05rem", lineHeight: 1.7, margin: "-1rem 0 2.5rem" }}>
                From a single farmer's surplus crops to a thriving co-op with 100+ vendors — here's how we got here.
              </p>
            </motion.div>

            <div style={{ position: "relative" }}>
              {/* Vertical line */}
              <div style={{ position: "absolute", left: 19, top: 8, bottom: 8, width: 2, background: "linear-gradient(180deg, #6dc744, #4a8c25, #c6252d)", borderRadius: 2 }} />

              <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
                {TIMELINE.map(({ year, label, desc }, idx) => (
                  <motion.div
                    key={year}
                    variants={fadeUp}
                    style={{ display: "flex", gap: "1.5rem", alignItems: "flex-start", paddingBottom: idx < TIMELINE.length - 1 ? "2rem" : 0 }}
                  >
                    {/* Dot */}
                    <div style={{ flexShrink: 0, width: 40, display: "flex", justifyContent: "center", paddingTop: "0.2rem" }}>
                      <div style={{
                        width: 14, height: 14, borderRadius: "50%",
                        background: idx === TIMELINE.length - 1 ? "#c6252d" : "#6dc744",
                        border: `3px solid ${idx === TIMELINE.length - 1 ? "#fdecea" : "#f2fbee"}`,
                        boxShadow: `0 0 0 2px ${idx === TIMELINE.length - 1 ? "#c6252d" : "#6dc744"}`,
                        position: "relative", zIndex: 1,
                      }} />
                    </div>

                    {/* Content */}
                    <LightCard style={{ flex: 1, marginBottom: 0 }}>
                      <div style={{ display: "flex", gap: "0.75rem", alignItems: "baseline", flexWrap: "wrap", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: "0.78rem", fontWeight: 700, color: idx === TIMELINE.length - 1 ? "#c6252d" : "#2d6a14", textTransform: "uppercase", letterSpacing: "0.07em", background: idx === TIMELINE.length - 1 ? "#fdecea" : "#f2fbee", padding: "0.2rem 0.6rem", borderRadius: 999 }}>
                          {year}
                        </span>
                        <h3 style={{ margin: 0, fontWeight: 800, fontSize: "1rem", color: "#0d1b2a" }}>{label}</h3>
                      </div>
                      <p style={{ margin: 0, color: "#4a5568", fontSize: "0.95rem", lineHeight: 1.7 }}>{desc}</p>
                    </LightCard>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default OurStory;
